/**
 * Financeiro → Contas (a pagar / a receber) — v2. [F2 / migrations 017 + 028]
 *
 * Layout fiel à referência oficial de design:
 *   integracao-nova/financeiro/titam-crm-financeiro-contas-v2.png
 *
 * Estrutura da tela:
 *   1. Abas "Contas a pagar" / "Contas a receber" com a contagem de cada lado
 *      (rotas irmãs; o componente é o mesmo, parametrizado por `kind`).
 *   2. Barra de filtros: período · busca (descrição, fornecedor/cliente ou nº do
 *      documento) · categoria · status · Importar planilha · Nova despesa/receita.
 *   3. Cinco indicadores clicáveis (Vencidas, Vencem hoje, A vencer, Pagas no
 *      período, Total previsto) que também filtram a lista.
 *   4. Barra de ações em lote + tabela + paginação, tudo num cartão só.
 *   5. Drawer lateral de lançamento (criar/editar), com troca direta entre
 *      despesa e receita pelo link do cabeçalho.
 *
 * Os dados vêm inteiros de GET /financeiro/contas-pagar|receber (sem paginação
 * no servidor): período, busca, ordenação, paginação e os indicadores são
 * calculados aqui, o que mantém a troca de filtro instantânea e permite comparar
 * o período atual com o anterior sem uma segunda requisição.
 *
 * Campos centro de custo, competência, documento, conta financeira e comprovante
 * dependem da migration 028; sem ela o backend simplesmente não os devolve e a
 * tela segue funcionando com o que existe.
 *
 * Estilos em contas.css (prefixo `fc-`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getContasPagar, createContaPagar, updateContaPagar, deleteContaPagar,
  getContasReceber, createContaReceber, updateContaReceber, deleteContaReceber,
  getSaldos, getProjects, getClientes, uploadComprovante, getComprovanteUrl,
  fmtBRL,
} from '../../db/api.js'
import {
  fmtDate, toDateInput, today, daysUntil,
  PERIODO_PADRAO, iso, periodoRange, rangeAnterior, inRange,
  useFechaFora, PeriodoFiltro, listaPaginas,
} from './financeiroKit.jsx'
import './contas.css'

const ABERTO = ['prevista', 'atrasada', 'inadimplente']
const VENCIDA = ['atrasada', 'inadimplente']

// Centros de custo sugeridos no formulário. Texto livre no banco: os que já
// existem nos lançamentos do cliente entram na lista junto com estes.
const CENTROS_CUSTO = [
  'ADM - Administrativo',
  'COM - Comercial',
  'MKT - Marketing',
  'TEC - Tecnologia',
  'FIN - Financeiro',
  'OPE - Operações',
]

const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const CFG = {
  pagar: {
    tipo: 'pagar',
    titulo: 'Contas a pagar',
    novoBtn: 'Nova despesa',
    novoTitulo: 'Nova despesa',
    editTitulo: 'Editar despesa',
    salvarBtn: 'Criar despesa',
    trocarBtn: 'Criar receita',
    trocarRota: '/financeiro/contas-receber',
    partiLabel: 'Fornecedor',
    partiBusca: 'Pesquisar por descrição, fornecedor ou nº do documento…',
    parti: (r) => r.fornecedor,
    pagosLabel: 'Pagas no período',
    pagoStatus: 'paga',
    pagoLabel: 'Marcar como paga',
    pagos: ['paga'],
    dataRealField: 'data_pagamento',
    dataRealLabel: 'Data de pagamento',
    statusOpts: [
      { v: 'prevista', label: 'Prevista' },
      { v: 'paga', label: 'Paga' },
      { v: 'cancelada', label: 'Cancelada' },
    ],
    api: { list: getContasPagar, create: createContaPagar, update: updateContaPagar, del: deleteContaPagar },
  },
  receber: {
    tipo: 'receber',
    titulo: 'Contas a receber',
    novoBtn: 'Nova receita',
    novoTitulo: 'Nova receita',
    editTitulo: 'Editar receita',
    salvarBtn: 'Criar receita',
    trocarBtn: 'Criar despesa',
    trocarRota: '/financeiro/contas-pagar',
    partiLabel: 'Cliente',
    partiBusca: 'Pesquisar por descrição, cliente ou nº do documento…',
    parti: (r) => r.contact_nome || r.deal_titulo,
    pagosLabel: 'Recebidas no período',
    pagoStatus: 'recebida',
    pagoLabel: 'Marcar como recebida',
    pagos: ['recebida'],
    dataRealField: 'data_recebimento',
    dataRealLabel: 'Data de recebimento',
    statusOpts: [
      { v: 'prevista', label: 'Prevista' },
      { v: 'recebida', label: 'Recebida' },
      { v: 'inadimplente', label: 'Inadimplente' },
      { v: 'cancelada', label: 'Cancelada' },
    ],
    api: { list: getContasReceber, create: createContaReceber, update: updateContaReceber, del: deleteContaReceber },
  },
}

// Período, popover de filtro, paginação e o helper de "clicar fora" vivem no
// financeiroKit: a tela de Saldo usa exatamente os mesmos.

// ── Formatação ──────────────────────────────────────────────────────────────
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
// KPIs do design vêm arredondados (R$ 18.940)
const brl0 = (v) => `R$ ${Math.round(num(v)).toLocaleString('pt-BR')}`
// competência 'yyyy-mm-01' → 'Jan/2024'
const fmtCompetencia = (v) => {
  if (!v) return ''
  const [y, m] = String(v).slice(0, 10).split('-')
  return `${MESES_CURTOS[Number(m) - 1] || m}/${y}`
}
// só a 1ª linha: contas importadas trazem o dump da integração na descrição
const primeiraLinha = (s) => String(s || '').split('\n')[0].trim()

// Situação exibida na lista (o backend já deriva 'atrasada' de uma prevista vencida).
function situacao(r, cfg) {
  if (cfg.pagos.includes(r.status)) return { label: cfg.tipo === 'pagar' ? 'Paga' : 'Recebida', cls: 'paga' }
  if (r.status === 'cancelada') return { label: 'Cancelada', cls: 'cancelada' }
  if (r.status === 'inadimplente') return { label: 'Inadimplente', cls: 'inadimplente' }
  if (r.status === 'atrasada') return { label: 'Atrasada', cls: 'atrasada' }
  if (daysUntil(r.data_vencimento) === 0) return { label: 'Vence hoje', cls: 'hoje' }
  return { label: 'A vencer', cls: 'vencer' }
}

// ── Peças ───────────────────────────────────────────────────────────────────

/**
 * Indicador do topo. `delta` nulo (sem base de comparação no período anterior)
 * vira um traço, para o card não mudar de altura.
 */
function Kpi({ icon, cor, bg, label, value, delta, deltaLabel, bomQuandoSobe = true, active, onClick }) {
  const sobe = num(delta) >= 0
  const bom = sobe === bomQuandoSobe
  const abs = Math.abs(num(delta))
  const texto = abs >= 1000 ? '>999' : abs.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return (
    <button type="button" className={`fc-kpi${active ? ' active' : ''}`} onClick={onClick}
      title={`Filtrar: ${label}`}>
      <div className="fc-kpi-head">
        <span className="fc-kpi-icon" style={{ background: bg, color: cor }}>
          <Icon n={icon} s={17} sw={1.7} />
        </span>
        <span className="fc-kpi-label">{label}</span>
      </div>
      <div className="fc-kpi-value">{value}</div>
      {delta == null ? (
        <div className="fc-delta"><span className="fc-delta-rest">sem base de comparação</span></div>
      ) : (
        <div className={`fc-delta ${bom ? 'up' : 'down'}`}>
          <Icon n={sobe ? 'arrow_up' : 'arrow_down'} s={13} sw={2} />
          <b>{texto}%</b>
          <span className="fc-delta-rest">{deltaLabel}</span>
        </div>
      )}
    </button>
  )
}

/**
 * Menu "..." da linha. Fica em `position: fixed` ancorado no botão: a tabela
 * rola na horizontal e um menu absoluto seria cortado pelo overflow.
 */
function RowMenu({ r, cfg, aberto, onToggle, onFechar, onEditar, onPagar, onDuplicar, onExcluir, busy }) {
  const ref = useFechaFora(aberto, onFechar)
  const btnRef = useRef(null)
  const [pos, setPos] = useState(null)
  const emAberto = ABERTO.includes(r.status)

  useEffect(() => {
    if (!aberto) { setPos(null); return undefined }
    const ancorar = () => {
      const rect = btnRef.current?.getBoundingClientRect()
      if (!rect) return
      // abre para cima quando não há espaço abaixo (últimas linhas da página)
      const acima = window.innerHeight - rect.bottom < 240
      setPos({
        top: acima ? undefined : rect.bottom + 4,
        bottom: acima ? window.innerHeight - rect.top + 4 : undefined,
        right: window.innerWidth - rect.right,
      })
    }
    ancorar()
    window.addEventListener('resize', ancorar)
    window.addEventListener('scroll', onFechar, true)
    return () => {
      window.removeEventListener('resize', ancorar)
      window.removeEventListener('scroll', onFechar, true)
    }
  }, [aberto, onFechar])

  const baixarComprovante = async () => {
    onFechar()
    try {
      const { url } = await getComprovanteUrl(r.comprovante_path)
      window.open(url, '_blank', 'noopener')
    } catch (e) { alert(`Não foi possível abrir o comprovante: ${e.message}`) }
  }
  return (
    <div style={{ display: 'inline-block' }} ref={ref}>
      <button ref={btnRef} type="button" className="fc-icon-btn" onClick={onToggle} title="Ações" disabled={busy}>
        <Icon n="more" s={16} />
      </button>
      {aberto && pos && (
        <div className="fc-row-menu" style={pos}>
          <button type="button" onClick={() => { onFechar(); onEditar() }}>
            <Icon n="edit" s={15} /> Editar
          </button>
          {emAberto && (
            <button type="button" onClick={() => { onFechar(); onPagar() }}>
              <Icon n="check" s={15} /> {cfg.pagoLabel}
            </button>
          )}
          <button type="button" onClick={() => { onFechar(); onDuplicar() }}>
            <Icon n="page" s={15} /> Duplicar
          </button>
          {r.comprovante_path && (
            <button type="button" onClick={baixarComprovante}>
              <Icon n="download" s={15} /> Abrir comprovante
            </button>
          )}
          <button type="button" className="danger" onClick={() => { onFechar(); onExcluir() }}>
            <Icon n="trash" s={15} /> Excluir
          </button>
        </div>
      )}
    </div>
  )
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function ContasView({ kind }) {
  const cfg = CFG[kind]
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null

  const [rows, setRows] = useState([])
  const [outras, setOutras] = useState([])       // só p/ a contagem da outra aba
  const [contas, setContas] = useState([])       // contas financeiras (saldo)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [periodo, setPeriodo] = useState(PERIODO_PADRAO)
  // intervalo livre; só entra em uso quando periodo === 'custom'
  const [custom, setCustom] = useState(() => {
    const r = periodoRange(PERIODO_PADRAO)
    return { ini: iso(r.ini), fim: iso(r.fim) }
  })
  const [search, setSearch] = useState('')
  const [categoria, setCategoria] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')
  const [cardFilter, setCardFilter] = useState('')
  const [sort, setSort] = useState({ key: 'data_vencimento', dir: 'asc' })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [sel, setSel] = useState(() => new Set())
  const [menuId, setMenuId] = useState(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [drawer, setDrawer] = useState(null)     // { item } | null
  const [importar, setImportar] = useState(false)
  const [busy, setBusy] = useState(null)

  const load = useCallback(async () => {
    if (!clientId) { setRows([]); setOutras([]); setLoading(false); return }
    setLoading(true); setErr(null)
    const outraApi = kind === 'pagar' ? getContasReceber : getContasPagar
    try {
      const [minhas, dela, saldos] = await Promise.all([
        cfg.api.list(),
        outraApi().catch(() => []),
        getSaldos().catch(() => []),
      ])
      setRows(minhas); setOutras(dela); setContas(saldos)
    } catch (e) { setErr(e.message) } finally { setLoading(false) }
  }, [clientId, cfg, kind])

  useEffect(() => { setDrawer(null); setPage(1); setSel(new Set()); load() }, [load])
  useEffect(() => { setPage(1); setSel(new Set()) }, [search, categoria, statusFiltro, cardFilter, periodo, custom, perPage])

  const range = useMemo(() => periodoRange(periodo, custom), [periodo, custom])
  const doPeriodo = useMemo(() => rows.filter((r) => inRange(r.data_vencimento, range)), [rows, range])

  const categorias = useMemo(() => {
    const set = new Set(rows.map((r) => r.categoria).filter(Boolean))
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [rows])

  const centrosCusto = useMemo(() => {
    const set = new Set([...CENTROS_CUSTO, ...rows.map((r) => r.centro_custo).filter(Boolean)])
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [rows])

  const fornecedores = useMemo(() => {
    const set = new Set(rows.map((r) => r.fornecedor).filter(Boolean))
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [rows])

  // Totais dos cards para uma janela qualquer — reusado no período atual e no
  // anterior (base da variação percentual).
  const totais = useCallback((janela) => {
    const lista = rows.filter((r) => inRange(r.data_vencimento, janela))
    const soma = (pred) => lista.filter(pred).reduce((s, r) => s + num(r.valor), 0)
    const aberto = (r) => ABERTO.includes(r.status)
    return {
      vencidas: soma((r) => VENCIDA.includes(r.status)),
      hoje: soma((r) => aberto(r) && daysUntil(r.data_vencimento) === 0),
      vencer: soma((r) => aberto(r) && daysUntil(r.data_vencimento) > 0),
      pagas: soma((r) => cfg.pagos.includes(r.status)),
      total: soma((r) => r.status !== 'cancelada'),
    }
  }, [rows, cfg])

  // Valores dos cards = período filtrado; a variação compara com a janela de
  // mesmo tamanho imediatamente anterior. Sempre há um período em vigor (o
  // seletor não tem mais "Todo o período"), então não existe caso sem janela.
  const { atual, anterior, deltaLabel } = useMemo(() => ({
    atual: totais(range),
    anterior: totais(rangeAnterior(range)),
    deltaLabel: 'vs. período anterior',
  }), [range, totais])

  // Sem base no período anterior não há percentual — o card mostra o aviso.
  const delta = (k) => {
    const ant = anterior[k]
    if (!ant) return null
    return ((atual[k] - ant) / Math.abs(ant)) * 100
  }

  // `bomQuandoSobe` define a cor da variação: só em Vencidas subir é ruim
  // (vermelho); nos demais a leitura é a do design — subiu, verde.
  const cards = [
    { id: 'vencidas', label: 'Vencidas', icon: 'briefcase', cor: '#d92d20', bg: '#fdeceb', valor: atual.vencidas, bomQuandoSobe: false },
    { id: 'hoje', label: 'Vencem hoje', icon: 'inbox', cor: '#f59e0b', bg: '#fff4e5', valor: atual.hoje, bomQuandoSobe: true },
    { id: 'vencer', label: 'A vencer', icon: 'arrow_down_circle', cor: '#2563eb', bg: '#eef4ff', valor: atual.vencer, bomQuandoSobe: true },
    { id: 'pagas', label: cfg.pagosLabel, icon: 'chart_up', cor: '#12805c', bg: '#e7f7ee', valor: atual.pagas, bomQuandoSobe: true },
    { id: 'total', label: 'Total previsto', icon: 'calculator', cor: '#667085', bg: '#f2f4f7', valor: atual.total, bomQuandoSobe: true },
  ]

  const passaCard = (r) => {
    if (!cardFilter) return true
    const aberto = ABERTO.includes(r.status)
    if (cardFilter === 'vencidas') return VENCIDA.includes(r.status)
    if (cardFilter === 'hoje') return aberto && daysUntil(r.data_vencimento) === 0
    if (cardFilter === 'vencer') return aberto && daysUntil(r.data_vencimento) > 0
    if (cardFilter === 'pagas') return cfg.pagos.includes(r.status)
    if (cardFilter === 'total') return r.status !== 'cancelada'
    return true
  }

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase()
    const out = doPeriodo.filter((r) => {
      if (!passaCard(r)) return false
      if (categoria && (r.categoria || '') !== categoria) return false
      if (statusFiltro && situacao(r, cfg).cls !== statusFiltro) return false
      if (q && ![r.descricao, r.fornecedor, r.contact_nome, r.deal_titulo, r.documento, r.categoria, r.centro_custo]
        .some((v) => (v || '').toLowerCase().includes(q))) return false
      return true
    })
    const { key, dir } = sort
    const mul = dir === 'asc' ? 1 : -1
    return [...out].sort((a, b) => {
      if (key === 'valor') return (num(a.valor) - num(b.valor)) * mul
      return String(a[key] || '').localeCompare(String(b[key] || '')) * mul
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doPeriodo, cardFilter, categoria, statusFiltro, search, sort, cfg])

  const totalPages = Math.max(1, Math.ceil(filtradas.length / perPage))
  const pageAtual = Math.min(page, totalPages)
  const pageRows = filtradas.slice((pageAtual - 1) * perPage, pageAtual * perPage)
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))

  const pageIds = pageRows.map((r) => r.id)
  const todosSel = pageIds.length > 0 && pageIds.every((id) => sel.has(id))
  const toggleTodos = () => setSel((prev) => {
    const n = new Set(prev)
    if (todosSel) pageIds.forEach((id) => n.delete(id)); else pageIds.forEach((id) => n.add(id))
    return n
  })
  const toggleUm = (id) => setSel((prev) => {
    const n = new Set(prev)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  // ── Mutações ──────────────────────────────────────────────────────────────
  const marcarPago = async (r) => {
    setBusy(r.id)
    try { await cfg.api.update(r.id, { status: cfg.pagoStatus, [cfg.dataRealField]: today() }); await load() }
    catch (e) { alert(`Erro: ${e.message}`) } finally { setBusy(null) }
  }
  const duplicar = async (r) => {
    setBusy(r.id)
    const copia = {
      descricao: `${primeiraLinha(r.descricao)} (cópia)`,
      valor: num(r.valor),
      data_vencimento: toDateInput(r.data_vencimento),
      categoria: r.categoria || null,
      centro_custo: r.centro_custo || null,
      documento: r.documento || null,
      competencia: r.competencia ? toDateInput(r.competencia) : null,
      data_emissao: r.data_emissao ? toDateInput(r.data_emissao) : null,
      conta_id: r.conta_id || null,
      status: 'prevista',
      recorrente: !!r.recorrente,
    }
    if (kind === 'pagar') copia.fornecedor = r.fornecedor || null
    else copia.contact_id = r.contact_id || null
    try { await cfg.api.create(copia); await load() }
    catch (e) { alert(`Erro ao duplicar: ${e.message}`) } finally { setBusy(null) }
  }
  const excluir = async (r) => {
    if (!window.confirm(`Excluir “${primeiraLinha(r.descricao)}”? Esta ação não pode ser desfeita.`)) return
    setBusy(r.id)
    try { await cfg.api.del(r.id); await load() }
    catch (e) { alert(`Erro ao excluir: ${e.message}`) } finally { setBusy(null) }
  }
  const bulkStatus = async (status) => {
    setBulkOpen(false); setBusy('bulk')
    const extra = cfg.pagos.includes(status) ? { [cfg.dataRealField]: today() } : {}
    try {
      for (const id of sel) await cfg.api.update(id, { status, ...extra })
      setSel(new Set()); await load()
    } catch (e) { alert(`Erro: ${e.message}`) } finally { setBusy(null) }
  }
  const bulkExcluir = async () => {
    if (!sel.size) return
    if (!window.confirm(`Excluir ${sel.size} lançamento(s)? Esta ação não pode ser desfeita.`)) return
    setBulkOpen(false); setBusy('bulk')
    try { for (const id of sel) await cfg.api.del(id); setSel(new Set()); await load() }
    catch (e) { alert(`Erro: ${e.message}`) } finally { setBusy(null) }
  }

  const bulkRef = useFechaFora(bulkOpen, useCallback(() => setBulkOpen(false), []))
  const bc = [{ label: 'Financeiro' }, { label: 'Contas' }]

  if (!clientId) {
    return (
      <Layout title="Contas" breadcrumb={bc}>
        <div className="fc-state">Selecione um cliente para ver as contas.</div>
      </Layout>
    )
  }

  return (
    <Layout title="Contas" breadcrumb={bc}>
      <div className="fc-wrap">
        {/* ── Abas ─────────────────────────────────────────────────────────── */}
        <div className="fc-tabs">
          <Link className={`fc-tab${kind === 'pagar' ? ' active' : ''}`} to="/financeiro/contas-pagar">
            Contas a pagar
            <span className="fc-tab-count">{kind === 'pagar' ? rows.length : outras.length}</span>
          </Link>
          <Link className={`fc-tab${kind === 'receber' ? ' active' : ''}`} to="/financeiro/contas-receber">
            Contas a receber
            <span className="fc-tab-count">{kind === 'receber' ? rows.length : outras.length}</span>
          </Link>
        </div>

        {/* ── Filtros ──────────────────────────────────────────────────────── */}
        <div className="fc-filters">
          <PeriodoFiltro periodo={periodo} custom={custom}
            onChange={(id, intervalo) => { if (intervalo) setCustom(intervalo); setPeriodo(id) }} />

          <div className="fc-search">
            <span className="fc-search-icon"><Icon n="search" s={15} /></span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={cfg.partiBusca} />
          </div>

          <select className="fc-ctrl" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Todas as categorias</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <select className="fc-ctrl" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="atrasada">Atrasada</option>
            <option value="hoje">Vence hoje</option>
            <option value="vencer">A vencer</option>
            <option value="paga">{kind === 'pagar' ? 'Paga' : 'Recebida'}</option>
            {kind === 'receber' && <option value="inadimplente">Inadimplente</option>}
            <option value="cancelada">Cancelada</option>
          </select>

          <div className="fc-filters-right">
            <button type="button" className="fc-ctrl" onClick={() => setImportar(true)}>
              <Icon n="upload" s={15} c="#667085" /> Importar planilha
            </button>
            <button type="button" className="fc-ctrl primary" onClick={() => setDrawer({ item: null })}>
              <Icon n="plus" s={15} sw={2} /> {cfg.novoBtn}
            </button>
          </div>
        </div>

        {/* ── Indicadores ──────────────────────────────────────────────────── */}
        <div className="fc-kpis">
          {cards.map((c) => (
            <Kpi key={c.id} icon={c.icon} cor={c.cor} bg={c.bg} label={c.label}
              value={brl0(c.valor)} delta={delta(c.id)} deltaLabel={deltaLabel}
              bomQuandoSobe={c.bomQuandoSobe} active={cardFilter === c.id}
              onClick={() => setCardFilter((f) => (f === c.id ? '' : c.id))} />
          ))}
        </div>

        {err && <div className="fc-error">{err}</div>}

        {/* ── Ações em lote ────────────────────────────────────────────────── */}
        <div className="fc-bulk">
          <input type="checkbox" className="fc-check" checked={todosSel} onChange={toggleTodos}
            aria-label="Selecionar todos da página" />
          <span className="fc-bulk-count">{sel.size} selecionados</span>
          <div className="fc-pop" ref={bulkRef}>
            <button type="button" className="fc-ctrl" disabled={!sel.size || busy === 'bulk'}
              onClick={() => setBulkOpen((o) => !o)}>
              Ações em lote
              <span className="fc-ctrl-caret"><Icon n="chevron_d" s={13} /></span>
            </button>
            {bulkOpen && (
              <div className="fc-pop-menu">
                <button type="button" className="fc-pop-item" onClick={() => bulkStatus(cfg.pagoStatus)}>
                  {cfg.pagoLabel}
                </button>
                <button type="button" className="fc-pop-item" onClick={() => bulkStatus('prevista')}>
                  Marcar como prevista
                </button>
                <button type="button" className="fc-pop-item" onClick={() => bulkStatus('cancelada')}>
                  Cancelar lançamentos
                </button>
              </div>
            )}
          </div>
          <button type="button" className="fc-icon-btn danger" title="Excluir selecionados"
            disabled={!sel.size || busy === 'bulk'} onClick={bulkExcluir}>
            <Icon n="trash" s={16} />
          </button>
        </div>

        {/* ── Lista ────────────────────────────────────────────────────────── */}
        <div className="fc-card">
          {loading ? (
            <div className="fc-state">Carregando…</div>
          ) : !filtradas.length ? (
            <div className="fc-state">
              {rows.length ? 'Nenhum lançamento corresponde aos filtros.' : `Nenhuma conta ${kind === 'pagar' ? 'a pagar' : 'a receber'} lançada ainda.`}
            </div>
          ) : (
            <>
              <div className="fc-table-scroll">
                <table className="fc-table">
                  <thead>
                    <tr>
                      <th className="fc-col-check">
                        <input type="checkbox" className="fc-check" checked={todosSel} onChange={toggleTodos}
                          aria-label="Selecionar todos da página" />
                      </th>
                      <Th label="Vencimento" k="data_vencimento" sort={sort} onSort={toggleSort} />
                      <Th label="Descrição" k="descricao" sort={sort} onSort={toggleSort} />
                      <th>{cfg.partiLabel}</th>
                      <th>Categoria</th>
                      <th>Centro de custo</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th className="fc-col-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r) => {
                      const aberto = ABERTO.includes(r.status)
                      const atrasoDias = aberto ? daysUntil(r.data_vencimento) : null
                      const atrasada = atrasoDias != null && atrasoDias < 0
                      const st = situacao(r, cfg)
                      const checked = sel.has(r.id)
                      const sub = fmtCompetencia(r.competencia) ||
                        (r.parcela && r.total_parcelas ? `Parcela ${r.parcela}/${r.total_parcelas}` : '') ||
                        (r.documento ? `Doc. ${r.documento}` : '')
                      return (
                        <tr key={r.id} className={`${atrasada ? 'overdue' : ''}${checked ? ' selected' : ''}`}>
                          <td className="fc-col-check">
                            <input type="checkbox" className="fc-check" checked={checked}
                              onChange={() => toggleUm(r.id)} aria-label="Selecionar lançamento" />
                          </td>
                          <td>
                            <div className={`fc-date${atrasada ? ' late' : ''}`}>{fmtDate(r.data_vencimento)}</div>
                            {atrasada && <div className="fc-date-tag">atrasada {Math.abs(atrasoDias)}d</div>}
                          </td>
                          <td>
                            <div className="fc-desc" title={primeiraLinha(r.descricao)}>{primeiraLinha(r.descricao)}</div>
                            {sub && <div className="fc-desc-sub">{sub}</div>}
                          </td>
                          <td className="fc-cell-clip" title={cfg.parti(r) || ''}>{cfg.parti(r) || '—'}</td>
                          <td className="fc-cell-clip" title={r.categoria || ''}>{r.categoria || '—'}</td>
                          <td className="fc-cell-clip" title={r.centro_custo || ''}>{r.centro_custo || '—'}</td>
                          <td className="fc-valor">{fmtBRL(r.valor)}</td>
                          <td><span className={`fc-pill ${st.cls}`}>{st.label}</span></td>
                          <td className="fc-col-acoes">
                            <RowMenu r={r} cfg={cfg} busy={busy === r.id}
                              aberto={menuId === r.id}
                              onToggle={() => setMenuId((id) => (id === r.id ? null : r.id))}
                              onFechar={() => setMenuId(null)}
                              onEditar={() => setDrawer({ item: r })}
                              onPagar={() => marcarPago(r)}
                              onDuplicar={() => duplicar(r)}
                              onExcluir={() => excluir(r)} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fc-foot">
                <span className="fc-foot-info">
                  Mostrando {(pageAtual - 1) * perPage + 1} a {Math.min(pageAtual * perPage, filtradas.length)} de {filtradas.length} registros
                </span>
                <div className="fc-pages">
                  <button type="button" className="fc-page" disabled={pageAtual <= 1}
                    onClick={() => setPage(pageAtual - 1)} aria-label="Página anterior">
                    <Icon n="chevron_l" s={15} />
                  </button>
                  {listaPaginas(pageAtual, totalPages).map((p, i) => (
                    p === '…'
                      ? <span key={`gap${i}`} className="fc-page-gap">…</span>
                      : (
                        <button key={p} type="button"
                          className={`fc-page${p === pageAtual ? ' active' : ''}`}
                          onClick={() => setPage(p)}>
                          {p}
                        </button>
                      )
                  ))}
                  <button type="button" className="fc-page" disabled={pageAtual >= totalPages}
                    onClick={() => setPage(pageAtual + 1)} aria-label="Próxima página">
                    <Icon n="chevron_r" s={15} />
                  </button>
                </div>
                <select className="fc-ctrl" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} por página</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {drawer && (
        <ContaDrawer
          cfg={cfg} kind={kind} item={drawer.item}
          categorias={categorias} centrosCusto={centrosCusto} fornecedores={fornecedores} contas={contas}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load() }}
        />
      )}

      {importar && (
        <ImportarPlanilha cfg={cfg} kind={kind}
          onClose={() => setImportar(false)}
          onDone={() => { setImportar(false); load() }} />
      )}
    </Layout>
  )
}

function Th({ label, k, sort, onSort }) {
  const ativo = sort.key === k
  return (
    <th>
      <button type="button" className="fc-th-sort" onClick={() => onSort(k)}>
        {label}
        <Icon n={ativo ? (sort.dir === 'asc' ? 'chevron_u' : 'chevron_d') : 'chevrons_up_down'}
          s={12} c={ativo ? '#344054' : '#c3c8d2'} />
      </button>
    </th>
  )
}

// ── Drawer de lançamento ────────────────────────────────────────────────────
/**
 * Criar/editar despesa ou receita. Campos marcados com * no design são exigidos
 * aqui também, com uma exceção: conta financeira só é obrigatória quando o
 * cliente já tem alguma cadastrada (senão a tela travaria sem saída).
 *
 * "Nº de parcelas" acima de 1 (só na criação) gera um lançamento por parcela,
 * mês a mês a partir do vencimento informado, cada um com o valor digitado.
 */
function ContaDrawer({ cfg, kind, item, categorias, centrosCusto, fornecedores, contas, onClose, onSaved }) {
  const isEdit = !!item
  const [f, setF] = useState(() => ({
    descricao: item?.descricao || '',
    fornecedor: item?.fornecedor || '',
    contact_id: item?.contact_id || '',
    valor: item?.valor != null ? String(item.valor) : '',
    categoria: item?.categoria || '',
    centro_custo: item?.centro_custo || '',
    documento: item?.documento || '',
    competencia: toDateInput(item?.competencia),
    data_emissao: toDateInput(item?.data_emissao),
    data_vencimento: toDateInput(item?.data_vencimento),
    data_real: toDateInput(item?.[cfg.dataRealField]),
    status: item?.status && item.status !== 'atrasada' ? item.status : 'prevista',
    conta_id: item?.conta_id || '',
    recorrente: !!item?.recorrente,
    parcelas: '1',
    project_id: item?.project_id || '',
  }))
  const [arquivo, setArquivo] = useState(null)
  const [vinculoAberto, setVinculoAberto] = useState(!!(item?.project_id || (kind === 'pagar' && item?.contact_id)))
  const [sugestoes, setSugestoes] = useState(false)
  const [projetos, setProjetos] = useState([])
  const [clientes, setClientes] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const fileRef = useRef(null)

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))

  // Listas do vínculo (e do campo Cliente em contas a receber) — carregadas só
  // quando a seção é aberta, para o drawer abrir instantâneo.
  //
  // Clientes vêm de /crm/clientes (getClientes), a mesma origem da página
  // Comercial → Clientes: contato com pelo menos um negócio ganho. Usar a lista
  // de contatos aqui traria todos os leads, que não são clientes.
  useEffect(() => {
    if (!vinculoAberto && kind === 'pagar') return
    let vivo = true
    Promise.all([getProjects().catch(() => []), getClientes().catch(() => [])])
      .then(([ps, cs]) => { if (vivo) { setProjetos(ps || []); setClientes(cs || []) } })
    return () => { vivo = false }
  }, [vinculoAberto, kind])

  const fornecedoresFiltrados = useMemo(() => {
    const q = f.fornecedor.trim().toLowerCase()
    return fornecedores.filter((n) => !q || n.toLowerCase().includes(q)).slice(0, 8)
  }, [fornecedores, f.fornecedor])

  const salvar = async () => {
    if (saving) return
    if (!f.descricao.trim()) return setErr('Informe a descrição.')
    if (kind === 'pagar' ? !f.fornecedor.trim() : !f.contact_id) return setErr(`Informe o ${cfg.partiLabel.toLowerCase()}.`)
    if (f.valor === '' || isNaN(Number(f.valor))) return setErr('Informe um valor válido.')
    if (!f.categoria.trim()) return setErr('Selecione a categoria.')
    if (!f.centro_custo.trim()) return setErr('Selecione o centro de custo.')
    if (!f.competencia) return setErr('Informe a competência.')
    if (!f.data_vencimento) return setErr('Informe o vencimento.')
    if (contas.length && !f.conta_id) return setErr('Selecione a conta financeira.')

    const parcelas = Math.max(1, Math.min(360, Number(f.parcelas) || 1))
    setSaving(true); setErr(null)

    // Comprovante primeiro: se o upload falhar, nada é gravado pela metade.
    let comprovante = null
    if (arquivo) {
      try { comprovante = { comprovante_path: await uploadComprovante(arquivo, kind), comprovante_nome: arquivo.name } }
      catch (e) { setSaving(false); return setErr(`Falha ao enviar o comprovante: ${e.message}`) }
    }

    const base = {
      descricao: f.descricao.trim(),
      valor: Number(f.valor),
      categoria: f.categoria.trim() || null,
      centro_custo: f.centro_custo.trim() || null,
      documento: f.documento.trim() || null,
      competencia: f.competencia || null,
      data_emissao: f.data_emissao || null,
      data_vencimento: f.data_vencimento,
      [cfg.dataRealField]: f.data_real || null,
      status: f.data_real ? cfg.pagoStatus : f.status,
      conta_id: f.conta_id || null,
      recorrente: f.recorrente,
      project_id: f.project_id || null,
      ...(comprovante || {}),
    }
    if (kind === 'pagar') {
      base.fornecedor = f.fornecedor.trim() || null
      base.contact_id = f.contact_id || null
    } else {
      base.contact_id = f.contact_id || null
    }

    try {
      if (isEdit) {
        await cfg.api.update(item.id, base)
      } else if (parcelas > 1) {
        const [y, m, d] = f.data_vencimento.split('-').map(Number)
        for (let i = 0; i < parcelas; i++) {
          const venc = new Date(y, m - 1 + i, d)
          await cfg.api.create({
            ...base,
            data_vencimento: iso(venc),
            parcela: i + 1,
            total_parcelas: parcelas,
            // só a 1ª parcela leva a baixa informada; as demais nascem previstas
            [cfg.dataRealField]: i === 0 ? base[cfg.dataRealField] : null,
            status: i === 0 ? base.status : 'prevista',
          })
        }
      } else {
        await cfg.api.create(base)
      }
      onSaved()
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const escolherArquivo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { setErr('O comprovante deve ter até 10MB.'); return }
    setArquivo(file); setErr(null)
  }

  return (
    <>
      <div className="fc-drawer-overlay" onMouseDown={onClose} />
      <aside className="fc-drawer" role="dialog" aria-label={isEdit ? cfg.editTitulo : cfg.novoTitulo}>
        <div className="fc-drawer-head">
          <div className="fc-drawer-title">{isEdit ? cfg.editTitulo : cfg.novoTitulo}</div>
          {!isEdit && (
            <Link className="fc-drawer-switch" to={cfg.trocarRota}>{cfg.trocarBtn}</Link>
          )}
          <button type="button" className="fc-drawer-x" onClick={onClose} aria-label="Fechar">
            <Icon n="close" s={18} />
          </button>
        </div>

        <div className="fc-drawer-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Descrição <i>*</i></label>
            <input className="fc-input" value={f.descricao} onChange={set('descricao')}
              placeholder="Ex.: Hospedagem site Jan/2024" autoFocus />
          </div>

          {kind === 'pagar' ? (
            <div className="fc-field" style={{ position: 'relative' }}>
              <label className="fc-label">Fornecedor <i>*</i></label>
              <div className="fc-input-icon">
                <input className="fc-input" value={f.fornecedor}
                  onChange={(e) => { set('fornecedor')(e); setSugestoes(true) }}
                  onFocus={() => setSugestoes(true)}
                  onBlur={() => setTimeout(() => setSugestoes(false), 120)}
                  placeholder="Pesquisar fornecedor" />
                <span><Icon n="search" s={15} /></span>
              </div>
              {sugestoes && fornecedoresFiltrados.length > 0 && (
                <div className="fc-suggest">
                  {fornecedoresFiltrados.map((n) => (
                    <button key={n} type="button" onMouseDown={() => setF((s) => ({ ...s, fornecedor: n }))}>{n}</button>
                  ))}
                </div>
              )}
              <div className="fc-hint">
                Fornecedor novo: basta digitar o nome — ele passa a aparecer nas sugestões.
              </div>
            </div>
          ) : (
            <div className="fc-field">
              <label className="fc-label">Cliente <i>*</i></label>
              <select className="fc-select" value={f.contact_id} onChange={set('contact_id')}>
                <option value="">Selecione…</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_nome || c.empresa ? `${c.nome} — ${c.company_nome || c.empresa}` : c.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="fc-grid-3 fc-field">
            <div>
              <label className="fc-label">Valor (R$) <i>*</i></label>
              <input className="fc-input" type="number" step="0.01" value={f.valor}
                onChange={set('valor')} placeholder="0,00" />
            </div>
            <div>
              <label className="fc-label">Categoria <i>*</i></label>
              <input className="fc-input" list="fc-categorias" value={f.categoria}
                onChange={set('categoria')} placeholder="Selecione…" />
              <datalist id="fc-categorias">
                {categorias.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="fc-label">Centro de custo <i>*</i></label>
              <input className="fc-input" list="fc-centros" value={f.centro_custo}
                onChange={set('centro_custo')} placeholder="Selecione…" />
              <datalist id="fc-centros">
                {centrosCusto.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Datas: emissão · vencimento · pagamento/recebimento na primeira
              linha; competência abaixo da emissão, na mesma coluna. */}
          <div className="fc-grid-3 fc-field">
            <div className="fc-cell">
              <label className="fc-label">Data de emissão</label>
              <input className="fc-input" type="date" value={f.data_emissao} onChange={set('data_emissao')} />
            </div>
            <div className="fc-cell">
              <label className="fc-label">Vencimento <i>*</i></label>
              <input className="fc-input" type="date" value={f.data_vencimento} onChange={set('data_vencimento')} />
            </div>
            <div className="fc-cell">
              <label className="fc-label">{cfg.dataRealLabel}</label>
              <input className="fc-input" type="date" value={f.data_real} onChange={set('data_real')} />
              <div className="fc-hint">Deixe em branco se ainda não {kind === 'pagar' ? 'pago' : 'recebido'}</div>
            </div>
            <div className="fc-cell">
              <label className="fc-label">Competência <i>*</i></label>
              <input className="fc-input" type="date" value={f.competencia} onChange={set('competencia')} />
            </div>
          </div>

          <div className="fc-grid-2 fc-field">
            <div>
              <label className="fc-label">Status <i>*</i></label>
              <select className="fc-select" value={f.status} onChange={set('status')}>
                {cfg.statusOpts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="fc-label">Conta financeira {contas.length ? <i>*</i> : null}</label>
              <select className="fc-select" value={f.conta_id} onChange={set('conta_id')}>
                <option value="">{contas.length ? 'Selecione…' : 'Nenhuma conta cadastrada'}</option>
                {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          <div className="fc-field">
            <label className="fc-label">Nº do documento</label>
            <input className="fc-input" value={f.documento} onChange={set('documento')}
              placeholder="Nota fiscal, boleto, recibo…" />
          </div>

          <div className="fc-box">
            <div className="fc-toggle-row">
              <button type="button" className={`fc-toggle${f.recorrente ? ' on' : ''}`}
                role="switch" aria-checked={f.recorrente} aria-label="Lançamento recorrente"
                onClick={() => setF((s) => ({ ...s, recorrente: !s.recorrente }))} />
              <div className="fc-toggle-text">
                Lançamento recorrente
                <span>Repita esta {kind === 'pagar' ? 'despesa' : 'receita'} automaticamente</span>
              </div>
            </div>

            {!isEdit && (
              <div className="fc-field" style={{ marginTop: 16, marginBottom: 0 }}>
                <div className="fc-label-row">
                  <label className="fc-label">Nº de parcelas</label>
                  <span className="fc-info-icon"
                    title="Cria um lançamento por parcela, mês a mês a partir do vencimento, cada um com o valor informado.">
                    <Icon n="info" s={13} />
                  </span>
                </div>
                <input className="fc-input" type="number" min="1" max="360" value={f.parcelas}
                  onChange={set('parcelas')} style={{ maxWidth: 130 }} />
              </div>
            )}

            <div className="fc-field" style={{ marginTop: 16, marginBottom: 0 }}>
              <label className="fc-label">Comprovante (recibo, nota fiscal, etc.)</label>
              <div className="fc-file-row">
                <button type="button" className="fc-file-btn" onClick={() => fileRef.current?.click()}>
                  <Icon n="upload" s={15} /> Selecionar arquivo
                </button>
                <span className="fc-file-note" title={arquivo?.name || item?.comprovante_nome || ''}>
                  {arquivo?.name || item?.comprovante_nome || 'PDF, JPG ou PNG até 10MB'}
                </span>
                <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                  onChange={escolherArquivo} />
              </div>
            </div>
          </div>

          <button type="button" className="fc-linkrow" onClick={() => setVinculoAberto((v) => !v)}>
            Vincular a projeto {kind === 'pagar' ? 'ou cliente ' : ''}(opcional)
            <Icon n={vinculoAberto ? 'chevron_d' : 'chevron_r'} s={16} />
          </button>

          {vinculoAberto && (
            <div className="fc-grid-2" style={{ marginTop: 14 }}>
              <div>
                <label className="fc-label">Projeto</label>
                <select className="fc-select" value={f.project_id} onChange={set('project_id')}>
                  <option value="">Nenhum</option>
                  {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome || p.titulo}</option>)}
                </select>
              </div>
              {kind === 'pagar' && (
                <div>
                  <label className="fc-label">Cliente</label>
                  <select className="fc-select" value={f.contact_id} onChange={set('contact_id')}>
                    <option value="">Nenhum</option>
                    {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_nome || c.empresa ? `${c.nome} — ${c.company_nome || c.empresa}` : c.nome}
                  </option>
                ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="fc-drawer-foot">
          <button type="button" className="fc-ctrl" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="fc-ctrl primary" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : cfg.salvarBtn}
          </button>
        </div>
      </aside>
    </>
  )
}

// ── Importação de planilha ──────────────────────────────────────────────────
/**
 * Importa lançamentos de um CSV (o formato que qualquer planilha exporta).
 * Cabeçalhos aceitos (sem acento/caixa): descricao, valor, vencimento, emissao,
 * competencia, categoria, centro_custo, fornecedor|cliente, documento, status.
 * O arquivo é lido no navegador; cada linha vira um POST.
 */
// chaves já normalizadas (sem acento, minúsculas) — ver semAcento()
const CSV_COLUNAS = {
  descricao: 'descricao', historico: 'descricao',
  valor: 'valor', total: 'valor',
  vencimento: 'data_vencimento', data_vencimento: 'data_vencimento', data: 'data_vencimento',
  competencia: 'competencia',
  emissao: 'data_emissao', data_emissao: 'data_emissao', 'data de emissao': 'data_emissao',
  categoria: 'categoria',
  centro_custo: 'centro_custo', 'centro de custo': 'centro_custo',
  fornecedor: 'fornecedor', cliente: 'fornecedor',
  documento: 'documento', 'no do documento': 'documento', numero_documento: 'documento',
  status: 'status',
}

const semAcento = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/º/g, 'o').trim().toLowerCase()

// Split de linha CSV respeitando aspas (campos com vírgula/ponto e vírgula).
function splitCsv(linha, sep) {
  const out = []
  let atual = '', aspas = false
  for (let i = 0; i < linha.length; i++) {
    const c = linha[i]
    if (c === '"') {
      if (aspas && linha[i + 1] === '"') { atual += '"'; i++ } else aspas = !aspas
    } else if (c === sep && !aspas) { out.push(atual); atual = '' } else atual += c
  }
  out.push(atual)
  return out.map((v) => v.trim())
}

// '1.234,56' / 'R$ 1.234,56' / '1234.56' → 1234.56
function parseValor(v) {
  const s = String(v || '').replace(/[^\d,.-]/g, '')
  if (!s) return NaN
  const brasileiro = s.lastIndexOf(',') > s.lastIndexOf('.')
  return Number(brasileiro ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, ''))
}

// 'dd/mm/aaaa' ou 'aaaa-mm-dd' → 'aaaa-mm-dd'
function parseData(v) {
  const s = String(v || '').trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
  return ''
}

function ImportarPlanilha({ cfg, kind, onClose, onDone }) {
  const [linhas, setLinhas] = useState([])
  const [nomeArquivo, setNomeArquivo] = useState('')
  const [erros, setErros] = useState([])
  const [enviando, setEnviando] = useState(false)
  const [progresso, setProgresso] = useState(0)
  const fileRef = useRef(null)

  const ler = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setNomeArquivo(file.name)
    const texto = await file.text()
    const linhasBrutas = texto.split(/\r?\n/).filter((l) => l.trim())
    if (!linhasBrutas.length) { setErros(['Arquivo vazio.']); return }

    const sep = (linhasBrutas[0].match(/;/g) || []).length > (linhasBrutas[0].match(/,/g) || []).length ? ';' : ','
    const cabecalho = splitCsv(linhasBrutas[0], sep).map((h) => CSV_COLUNAS[semAcento(h)] || null)
    if (!cabecalho.includes('descricao') || !cabecalho.includes('valor') || !cabecalho.includes('data_vencimento')) {
      setLinhas([])
      setErros(['O arquivo precisa ter, no mínimo, as colunas descrição, valor e vencimento.'])
      return
    }

    const problemas = []
    const out = []
    linhasBrutas.slice(1).forEach((l, i) => {
      const campos = splitCsv(l, sep)
      const reg = {}
      cabecalho.forEach((col, j) => { if (col) reg[col] = campos[j] })
      const valor = parseValor(reg.valor)
      const venc = parseData(reg.data_vencimento)
      if (!reg.descricao || isNaN(valor) || !venc) {
        problemas.push(`Linha ${i + 2}: descrição, valor ou vencimento inválido.`)
        return
      }
      const competencia = parseData(reg.competencia) || (/^\d{2}\/\d{4}$/.test(reg.competencia || '')
        ? `${reg.competencia.slice(3)}-${reg.competencia.slice(0, 2)}-01` : '')
      out.push({
        descricao: reg.descricao,
        valor,
        data_vencimento: venc,
        categoria: reg.categoria || null,
        centro_custo: reg.centro_custo || null,
        documento: reg.documento || null,
        competencia: competencia || null,
        data_emissao: parseData(reg.data_emissao) || null,
        status: ['prevista', cfg.pagoStatus, 'cancelada'].includes(semAcento(reg.status)) ? semAcento(reg.status) : 'prevista',
        ...(kind === 'pagar' ? { fornecedor: reg.fornecedor || null } : {}),
      })
    })
    setLinhas(out); setErros(problemas)
  }

  const importar = async () => {
    setEnviando(true); setProgresso(0)
    const falhas = []
    for (let i = 0; i < linhas.length; i++) {
      try { await cfg.api.create(linhas[i]) }
      catch (e) { falhas.push(`Linha ${i + 2}: ${e.message}`) }
      setProgresso(i + 1)
    }
    setEnviando(false)
    if (falhas.length) { setErros(falhas); return }
    onDone()
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <div className="fc-drawer-title">Importar planilha</div>
          <button type="button" className="fc-drawer-x" onClick={onClose} aria-label="Fechar">
            <Icon n="close" s={18} />
          </button>
        </div>

        <div className="fc-modal-body">
          <button type="button" className="fc-drop" onClick={() => fileRef.current?.click()}>
            <Icon n="upload" s={22} c="#98a2b3" />
            <span>{nomeArquivo || 'Selecionar arquivo CSV'}</span>
            <span className="fc-hint" style={{ margin: 0 }}>
              Colunas: descrição, valor, vencimento, emissão, competência, categoria,
              centro de custo,{kind === 'pagar' ? ' fornecedor' : ' cliente'}, nº do documento, status.
            </span>
          </button>
          <input ref={fileRef} type="file" hidden accept=".csv,text/csv" onChange={ler} />

          {erros.length > 0 && (
            <div className="fc-error" style={{ marginTop: 14 }}>
              {erros.slice(0, 5).map((m) => <div key={m}>{m}</div>)}
              {erros.length > 5 && <div>… e mais {erros.length - 5}.</div>}
            </div>
          )}

          {linhas.length > 0 && (
            <>
              <div className="fc-hint" style={{ marginTop: 14 }}>
                {linhas.length} lançamento(s) prontos para importar — prévia das 5 primeiras linhas:
              </div>
              <table className="fc-preview">
                <thead>
                  <tr><th>Vencimento</th><th>Descrição</th><th>{cfg.partiLabel}</th><th>Valor</th></tr>
                </thead>
                <tbody>
                  {linhas.slice(0, 5).map((l, i) => (
                    <tr key={i}>
                      <td>{fmtDate(l.data_vencimento)}</td>
                      <td>{l.descricao}</td>
                      <td>{l.fornecedor || '—'}</td>
                      <td>{fmtBRL(l.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="fc-modal-foot">
          <button type="button" className="fc-ctrl" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button type="button" className="fc-ctrl primary" onClick={importar}
            disabled={enviando || !linhas.length}>
            {enviando ? `Importando ${progresso}/${linhas.length}…` : `Importar ${linhas.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  )
}
